import type { Translations } from "./types.js";
import { formatRange } from "./format.js";

/**
 * English translation map.
 *
 * Must be COMPLETE — the `Translations` type is the source of truth,
 * and the English map is the fallback for any key missing in other
 * locale maps. TypeScript will error if a new key is added to
 * `Translations` without a corresponding entry here.
 */
export const en: Translations = {
  "languageSelector.label": "Language",
  "languageSelector.english": "English",
  "languageSelector.japanese": "Japanese",

  // Theme selector
  "theme.label": "Theme",
  "theme.system": "System",
  "theme.light": "Light",
  "theme.dark": "Dark",

  // Settings pane
  "settings.ai.title": "⚙ AI settings",
  "settings.security.heading": "⚠ About security",
  "settings.security.bodyBrowser":
    "This tool uses your Claude API key directly in the browser. The key is stored only in this browser and is never sent to any external server.",
  "settings.security.bodyXss":
    "However, the key could leak if this page is compromised by an XSS attack. We recommend issuing a karasu-specific restricted key from the Anthropic console.",
  "settings.security.linkLabel": "→ Manage keys on console.anthropic.com",
  "settings.apiKey.label": "Claude API key",
  "settings.persist.label": "Persist across sessions (save to localStorage)",
  "settings.persist.hint":
    "When off, the API key is cleared when the tab closes (recommended). Persisting keeps the key readable by any script running on this page across sessions — if the page is ever compromised (XSS), a persisted key is exposed for far longer.",
  "settings.save.saved": "✓ Saved",
  "settings.save.label": "💾 Save",
  "settings.clear.label": "🗑 Clear",

  // ProjectMode bootstrap error screen (#1530)
  "projectInit.error.title": "Failed to load your projects.",
  "projectInit.error.hint":
    "This can happen if browser storage (OPFS) is unavailable — e.g. in private browsing or when storage is full. Check your browser settings and reload.",

  // Project selector
  "projectSelector.namePlaceholder": "Project name",
  "projectSelector.new.title": "New project",
  "projectSelector.new.button": "+ New",
  "projectSelector.rename.title": "Rename project",
  "projectSelector.rename.button": "✎ Rename",
  "projectSelector.delete.title": "Delete project",
  "projectSelector.delete.button": "✕ Delete",
  "projectSelector.delete.confirm": ({ name }) => `Delete "${name}"?`,
  "projectSelector.export.title": "Export as ZIP",
  "projectSelector.export.button": "↓ Export",
  "projectSelector.import.title": "Import from ZIP",
  "projectSelector.import.button": "↑ Import",
  "projectSelector.translate.title": "Translate an infra config to .krs",
  "projectSelector.translate.button": "⇄ Translate",
  "projectSelector.ok": "OK",
  "projectSelector.cancel": "Cancel",
  "project.error.create": ({ detail }) => `⚠ Failed to create project: ${detail}`,
  "project.error.rename": ({ detail }) => `⚠ Failed to rename project: ${detail}`,
  "project.error.delete": ({ detail }) => `⚠ Failed to delete project: ${detail}`,
  "project.error.export": ({ detail }) => `⚠ Failed to export project: ${detail}`,
  "project.error.import": ({ detail }) => `⚠ Import failed: ${detail}`,
  "project.error.openExample": ({ detail }) => `⚠ Could not open the example: ${detail}`,
  "project.error.snapshot": ({ detail }) => `⚠ Failed to capture snapshot: ${detail}`,
  "project.error.dismiss": "✕ Dismiss",

  // Translate dialog
  "translateDialog.title": "⇄ Translate infra config to .krs",
  "translateDialog.description":
    "Convert a Docker Compose, Kubernetes, OpenAPI, or DB schema file into a .krs scaffold.",
  "translateDialog.format.label": "Input format",
  "translateDialog.format.compose": "Docker Compose",
  "translateDialog.format.k8s": "Kubernetes manifest",
  "translateDialog.format.openapi": "OpenAPI spec",
  "translateDialog.format.db": "DB schema (SQL)",
  "translateDialog.format.wrangler": "Cloudflare wrangler.toml",
  "translateDialog.loadHint": "Paste the source below, or load a file:",
  "translateDialog.loadFile": "📂 Load file…",
  "translateDialog.loadFile.aria": "Load a file",
  "translateDialog.sourceContent": "Source content",
  "translateDialog.advanced": "Advanced options",
  "translateDialog.sourceName.label":
    "Source name (used to derive the deploy / service / database name)",
  "translateDialog.sourceName.aria": "Source name",
  "translateDialog.service.label": "Service name (overrides info.title)",
  "translateDialog.service.aria": "Service name",
  "translateDialog.database": "Database name",
  "translateDialog.granularity.label": "Granularity",
  "translateDialog.granularity.resourceDefault": "resource (default)",
  "translateDialog.granularity.operation": "operation",
  "translateDialog.granularity.aggregateDefault": "aggregate (default)",
  "translateDialog.granularity.table": "table",
  "translateDialog.emitBindings": "Emit usecase → resource bindings",
  "translateDialog.emitCrudDecoration": "Decorate operations with <verb>:<crud>",
  "translateDialog.system.label": "Wrap in system block (optional)",
  "translateDialog.system.aria": "System name",
  "translateDialog.mapFile.label": "karasu.map.yaml content (optional — resolves `realizes`)",
  "translateDialog.mapFile.aria": "karasu.map.yaml content",
  "translateDialog.result": "Generated .krs",
  "translateDialog.close": "Close",
  "translateDialog.download": "↓ Download .krs",
  "translateDialog.copy": "⧉ Copy .krs",
  "translateDialog.translate": "⇄ Translate",
  "translateDialog.copyFailed":
    "Couldn't copy to the clipboard. Select the text and copy it manually.",
  "translateDialog.loadFailed": "Couldn't read the selected file.",

  // Chat pane
  "chat.newSession.button": "↺ New Session",
  "chat.startInterview.button": "▶ Start Interview",
  "chat.startReview.button": "🔍 Start Review",
  "chat.emptyState.hint": 'Or type freely (e.g. "Review this model")',
  "chat.message.userRole": "You",
  "chat.retry.button": "↺ Retry",
  "chat.openSettings.button": "⚙ Open Settings",
  "chat.loading": "AI is thinking…",
  "chat.input.placeholderPending": "Please review the patch before sending",
  "chat.input.placeholderDefault": "Type a message… (Cmd+Enter or Ctrl+Enter to send)",
  "chat.input.ariaLabel": "Chat message input",
  "chat.send.button": "↑ Send",
  "chat.patch.apply.button": "✓ Apply",
  "chat.patch.reject.button": "✕ Reject",
  "chat.apiKeySetup.message": "A Claude API key is required to use AI features.",
  "chat.apiKeySetup.goToSettings": "⚙ Configure in Settings",

  // Chat error messages
  "chat.error.auth": "⚠ The API key is invalid. Please set a valid key in Settings.",
  "chat.error.rateLimit": "⚠ Rate limit reached. Please wait a moment and retry.",
  "chat.error.server": "⚠ Anthropic server error. Please wait a moment and retry.",
  "chat.error.patchFailed": ({ detail }) => `⚠ Failed to apply patch: ${detail}`,

  // NodeDetailPanel
  "nodeDetail.close": "Close",
  "nodeDetail.links.title": "🔗 Links",
  "nodeDetail.openDeployView": "🚀 View in Deploy diagram →",
  "nodeDetail.jumpToEditor": "↗ Jump to editor",
  "nodeDetail.annotationDiff.title": "⇄ Annotation diff",
  "nodeDetail.resources.title": "📦 Storage resources",
  "nodeDetail.capabilities.title": "🔐 Capabilities",
  "nodeDetail.migration.title": "🕒 Migration intent",
  "nodeDetail.migration.until": "until",
  "nodeDetail.migration.from": "from",

  // Empty-state placeholders (rendered inside SVG by core renderers)
  "emptyState.deploy.title": "No deploy block defined",
  "emptyState.deploy.hint": "Add a deploy block to your .krs file",
  "emptyState.deploy.jobBand": "Scheduled jobs",
  "emptyState.deploy.unclassified": "Unclassified",
  "emptyState.org.noTeams": "No teams defined",
  "emptyState.system.noNodes": "No nodes to render",
  "emptyState.org.placeholder": "No org diagram",
  "emptyState.system.noDiagram": "No diagram",

  // Built-in annotation badge labels (must match reference-data en labels)
  "badge.deprecated": "Deprecated",
  "badge.new": "NEW",
  "badge.experimental": "Experimental",
  "badge.draft": "Draft",
  "badge.planned": "Planned",
  "badge.migrationTarget": "Migration target",

  // ReferencePanel
  "referencePanel.unsupportedMessage": "Tags & Annotations are not available for this diagram.",
  "referencePanel.builtin.description":
    "Built-in default theme — applies to all diagram types (lowest cascade priority)",
  "referencePanel.samples.description": "Complete example — system + deploy + org",
  "referencePanel.copy.label": "Copy",
  "referencePanel.copy.copied": "Copied!",
  "referencePanel.experimental.badge": "experimental",
  "referencePanel.experimental.hint":
    "Backward compatibility is not yet promised for this notation. It stays experimental until real-usage evidence carries it through the promotion gate.",
  "referencePanel.grouping.construct": "Construct",
  "referencePanel.grouping.description": "Description",
  "referencePanel.grouping.membership": "Membership",
  "referencePanel.grouping.properties": "Properties",

  // Preview toolbar — export controls
  "preview.export.svg.label": "↓ Export SVG",
  "preview.export.svg.ariaLabel": "Export SVG",
  "preview.export.options.ariaLabel": "Export options",
  "preview.export.drillDown.label": "Export Drill-down SVG",
  "preview.export.allDiagrams.label": "Export All Diagrams SVG",
  "preview.export.drawio.label": "Export draw.io (mxGraph XML)",
  "preview.export.drawio.title":
    "Export to draw.io (mxGraph XML) — a layout escape hatch you can polish in diagrams.net",
  "preview.export.drawio.failed": ({ detail }) => `⚠ draw.io export failed: ${detail}`,

  // View-state toggles and window-opening controls
  "preview.iconMode.label": "Icon Mode",
  "preview.iconMode.ariaLabel": "Toggle icon mode",
  "preview.orgTree.label": "Tree View",
  "preview.orgTree.ariaLabel": "Toggle org tree view",
  "preview.entities.label": "Entities",
  "preview.entities.ariaLabel": "Toggle entity view",
  "preview.allLayers.label": "Show All Layers",
  "preview.allLayers.ariaLabel": "Toggle all layers",
  "preview.openAllViews.label": "Open All Views",
  "preview.openAllViews.ariaLabel": "Open all views in new window",
  "preview.focus.label": "Focus",
  "preview.focus.ariaLabel": "Enter focus mode",
  "preview.focus.exit.label": "Exit Focus",
  "preview.focus.exit.ariaLabel": "Exit focus mode",
  "preview.exportError.dismiss.label": "Dismiss",
  "preview.exportError.dismiss.ariaLabel": "Dismiss export error",
  // Facet overview panel (#2177)
  "facetOverview.title": "Facet membership",
  "facetOverview.close": "Close the facet overview",
  "facetOverview.open": "Membership overview",
  "facetOverview.derivedNote":
    "Derived from the model on every compile. Membership is written on the elements themselves — this list is never authored, so it cannot fall out of date.",
  "facetOverview.memberCount": ({ count }) => (count === 1 ? "1 element" : `${count} elements`),
  "facetOverview.noMembers": "No element declares this facet.",
  "facetOverview.undeclared": "Referenced but never declared.",
  "facetOverview.highlightHint": "Highlight this facet in the diagram",

  // Group by (system-view team grouping, #1858)
  "preview.facets.label": "Facets",
  "preview.facets.active": ({ count }) => `◎ Facets (${count})`,
  "preview.groupBy.label": "Group by",
  "preview.groupBy.none": "None",
  "preview.groupBy.team": "Team",
  "preview.groupBy.boundary": "Boundary",
  "preview.groupBy.collapseAll": "Collapse all",
  "preview.groupBy.expandAll": "Expand all",
  "preview.expansion.overloadHint":
    "Many containers expanded — Collapse all to return to the overview",

  // Share (inline URL sharing, karasu-nest)
  "preview.share.label": "🔗 Share",
  "preview.share.ariaLabel": "Share this diagram",
  "preview.share.dialog.title": "Share this diagram",
  "preview.share.dialog.description":
    "Anyone with a link can view this diagram — the content is encoded in the URL itself. Pick the link that fits how you want to share.",
  "preview.share.dialog.privateLabel": "Private link",
  "preview.share.dialog.privateHint":
    "Never sent to a server. But link previews (Slack, X, …) won't show the diagram.",
  "preview.share.dialog.privateUrlAriaLabel": "Private shareable URL",
  "preview.share.dialog.unfurlLabel": "Link with preview",
  "preview.share.dialog.unfurlHint":
    "Unfurls with the diagram in Slack, X, etc. The shared content is sent to the server (and may appear in logs).",
  "preview.share.dialog.unfurlUrlAriaLabel": "Shareable URL with preview",
  "preview.share.dialog.oversizeWarning":
    "This project is too large for a preview link. The private link still works.",
  "preview.share.dialog.includeTargetLabel": "Link to the current view",
  "preview.share.dialog.includeTargetHint":
    "The link opens drilled to where you are now (and highlights the focused element), instead of the whole model.",
  "preview.share.dialog.generating": "Generating link…",
  "preview.share.dialog.copy": "⧉ Copy",
  "preview.share.dialog.copied": "✓ Copied to clipboard",
  "preview.share.dialog.close": "Close",
  "preview.share.restoreFailed":
    "This shared link could not be restored. Opening a new project instead.",

  // Documentation links dropdown (in-app Reference + external docs site)
  "preview.docs.label": "📖 Docs",
  "preview.docs.ariaLabel": "Documentation links",
  "preview.docs.reference.label": "↗ Reference",
  "preview.docs.site.label": "↗ Documentation site",
  "preview.docs.site.ariaLabel": "Open the documentation site in a new tab",

  // Warnings (rendered in the WarningPanel)
  "warning.domainDispersal.message": ({ domainId }) =>
    `Domain "${domainId}" appears under multiple services`,
  "warning.domainDispersal.checkCohesion":
    "DDD sometimes calls cross-service domain reuse a cohesion smell",
  "warning.sharedInfraFanIn.message": ({ infraKind, infraId, count }) =>
    `${infraKind} "${infraId}" is shared by ${count} services`,
  "warning.sharedInfraFanIn.checkDatabasePerService":
    "Microservices Database-per-Service sometimes calls a shared store a smell",
  "warning.crossDomainStoreAccess.message": ({
    accessingDomain,
    infraKind,
    infraId,
    tableId,
    mode,
    ownerCount,
  }) => {
    const verb = mode === "write" ? "writes" : mode === "readwrite" ? "reads/writes" : "reads";
    const owner = ownerCount > 1 ? `${ownerCount} other domains` : "another domain";
    return `Domain "${accessingDomain}" ${verb} ${infraKind} leaf "${infraId}.${tableId}" owned by ${owner}`;
  },
  "warning.crossDomainStoreAccess.checkBoundary":
    "Reaching into another domain's store is sometimes called a boundary smell (legitimate under a shared kernel or during migration)",
  "warning.unassignedDomain.message": ({ display }) =>
    `Domain "${display}" is not assigned to any service`,
  "warning.unassignedUsecase.message": ({ usecaseId }) =>
    `Usecase "${usecaseId}" is not assigned to any domain`,
  "warning.unassignedResource.message": ({ resourceId }) =>
    `resource "${resourceId}" is not assigned to any database or entity`,
  "warning.entityAnchorCollision.message": ({ id }) =>
    `Entity anchor id "${id}" is claimed by more than one domain or entity; #krs-entity-${id} deep-links are ambiguous`,
  "warning.unassignedService.message": ({ display }) =>
    `Service "${display}" is not assigned to any system`,
  "warning.unassignedClient.message": ({ display }) =>
    `Client "${display}" is not assigned to any system`,
  "warning.unresolvedHandles.message": ({ nodeKind, nodeId, domainId }) =>
    `${nodeKind} "${nodeId}" declares handles "${domainId}" but no outgoing edge target exposes that domain`,
  "warning.unassignedDatabase.message": ({ display }) =>
    `Database "${display}" is not assigned to any system`,
  "warning.unassignedQueue.message": ({ display }) =>
    `Queue "${display}" is not assigned to any system`,
  "warning.unassignedStorage.message": ({ display }) =>
    `Storage "${display}" is not assigned to any system`,
  "warning.styleConflict.message": ({ selector }) =>
    `Selector "${selector}" is defined in multiple style files`,
  "warning.styleConflict.sheetLabel": ({ index }) => `Style file ${index + 1}`,
  "warning.missingRuntime.message": ({ nodeId }) =>
    `Deploy node "${nodeId}" has no runtime specified`,
  "warning.missingRealizes.message": ({ nodeId }) =>
    `Deploy node "${nodeId}" has no realizes specified`,
  "warning.unresolvedRealizes.message": ({ deployNodeId, target }) =>
    `Deploy node "${deployNodeId}" realizes "${target}" but no service or domain with that id exists`,
  "warning.invalidOwns.message": ({ teamId, ownedId, ownedKind }) =>
    `Team "${teamId}" owns "${ownedId}", but kind "${ownedKind}" cannot be owned. A team owns a service, a domain, a client or an infra block`,
  "warning.crossSystemRefUnresolved.message": ({ ref }) =>
    `"${ref}" could not be resolved — rendered as an unresolved external node`,
  "warning.unresolvedEdgeEndpoint.message": ({ from, to, unresolvedId }) =>
    `Edge "${from} → ${to}" references unknown node "${unresolvedId}"; the edge is dropped (the resolved endpoint is kept)`,
  "warning.edgeEndpointNotAtScope.message": ({
    from,
    to,
    endpointId,
    endpointKind,
    ownerId,
    ownerKind,
    scopeId,
    scopeKind,
  }) =>
    `Edge "${from} → ${to}" is declared in ${scopeKind} "${scopeId}", but "${endpointId}" is a ${endpointKind}${
      ownerId === "" ? "" : ` inside ${ownerKind} "${ownerId}"`
    } — not at that scope, so the edge renders on no view`,
  "warning.edgeEndpointNotAtScope.anchorHint": ({ from, to }) =>
    `Declare the edge inside its source block (\`${from} { -> ${to} }\`) so both ends are peers where it is drawn`,
  "warning.edgeEndpointNotAtScope.qualifyHint": ({ ownerId, endpointId }) =>
    `Reference the target qualified ("${ownerId}.${endpointId}") so the relation renders in the entity view`,
  "warning.crossSystemRefImplicitExternal.message": ({ ref, sourceSystemId, sourceNodeId }) =>
    `"${ref}" is referenced from ${sourceSystemId}.${sourceNodeId} but is not explicitly annotated as @external`,
  "warning.crossSystemRefImplicitExternal.suppressHint": ({ targetSystemId, sourceSystemId }) =>
    `Add 'service ${targetSystemId} [external]' to system ${sourceSystemId} to suppress this warning`,
  "warning.cyclicDependency.message": ({ path }) => `Circular dependency detected: ${path}`,
  "warning.deliversTargetNotClient.message": ({ serviceId, targetId }) =>
    `service "${serviceId}" delivers target "${targetId}" is not a client node`,
  "warning.clientCapabilityDuplicate.message": ({ clientId, name }) =>
    `client "${clientId}" declares capability "${name}" more than once`,
  "warning.annotationPossibleTypo.message": ({ nodeId, annotation, suggestion }) =>
    `"@${annotation}" on ${nodeId} — did you mean "@${suggestion}"?`,
  "warning.annotationPossibleTypo.openSetNote":
    "This hint only fires for names close to a built-in. Any name still parses in v1.x, but non-builtin names are deprecated (see annotation-not-builtin).",
  "warning.tagNotBuiltin.message": ({ nodeId, tag }) =>
    `"[${tag}]" on ${nodeId} is not a builtin tag — deprecated. Syntax v2.0 accepts tool vocabulary only (warned, files still parse).`,
  "warning.tagNotBuiltin.migrationNote":
    "For membership or model-specific labeling, migrate to the facet construct (#2065). For a missing archetype, request a builtin tag addition instead.",
  "warning.tagNotApplicable.message": ({ nodeId, tag, nodeKind, appliesTo }) =>
    `"[${tag}]" on ${nodeId} has no effect: the tag applies to ${appliesTo}, but ${nodeId} is a ${nodeKind}.`,
  "warning.tagNotApplicable.applicabilityNote":
    "A builtin tag only carries meaning on the kinds listed in its applicability. Remove it, or move it to a node of an applicable kind.",
  "warning.annotationNotBuiltin.message": ({ nodeId, annotation }) =>
    `"@${annotation}" on ${nodeId} is not a builtin annotation — deprecated. Syntax v2.0 accepts tool vocabulary only (warned, files still parse).`,
  "warning.annotationNotBuiltin.migrationNote":
    "For membership or model-specific labeling, migrate to the facet construct (#2065). For a missing lifecycle state, request a builtin annotation addition instead.",
  "warning.styleTagSelectorNotBuiltin.message": ({ tag, selector }) =>
    `The selector "${selector}" targets "[${tag}]", which is not a builtin tag — deprecated. The rule still applies in v1.x; syntax v2.0 matches tool vocabulary only.`,
  "warning.styleAnnotationSelectorNotBuiltin.message": ({ annotation, selector }) =>
    `The selector "${selector}" targets "@${annotation}", which is not a builtin annotation — deprecated. The rule still applies in v1.x; syntax v2.0 matches tool vocabulary only.`,
  "warning.styleSelectorNotBuiltin.migrationNote":
    'Migrate in three steps: declare the concern — facet <id> { label "..." } — add "facets <id>" to the elements, then rewrite the selector as [facets=<id>]. Specificity is unchanged, so nothing else in the cascade moves.',
  "warning.facetNotDeclared.message": ({ nodeId, facetId }) =>
    `"facets ${facetId}" on ${nodeId} names no declared facet`,
  "warning.facetNotDeclared.declarationNote":
    'Declare it at the top level — facet <id> { label "..." } — or fix the spelling to match an existing declaration.',
  "warning.legendRefUnresolved.message": ({ target, legendTitle }) =>
    legendTitle
      ? `legend "${legendTitle}": ref ${target} does not match any node or style rule`
      : `legend ref ${target} does not match any node or style rule`,
  "warning.styleColumnInvalidValue.message": ({ nodeId, value }) =>
    `column: "${value}" on #${nodeId} is not one of left / center / right — ignored`,
  "warning.styleColumnIgnoredNonSystemView.message": ({ nodeId, viewType }) =>
    `column hint on #${nodeId} is ignored in ${viewType} view (layout hints currently apply only to system view)`,
  "warning.styleGridColumnsInvalidValue.message": ({ nodeId, value }) =>
    `grid-columns: "${value}" on #${nodeId} is not a positive integer — ignored (layout auto-balances instead)`,
  "warning.styleInvalidEnumValue.message": ({ property, value, allowed }) =>
    `${property}: "${value}" is not one of ${allowed.join(" / ")} — ignored`,
  "warning.styleInvalidHexColor.message": ({ property, value }) =>
    `${property}: "${value}" is not a valid hex color (expected #RGB / #RGBA / #RRGGBB / #RRGGBBAA) — ignored`,
  "warning.styleMissingLengthUnit.message": ({ property, value, allowedUnits }) =>
    `${property}: "${value}" is missing a unit (expected ${allowedUnits.join(" / ")}) — ignored`,
  "warning.styleInvalidLengthUnit.message": ({ property, value, unit, allowedUnits }) =>
    `${property}: "${value}" uses unit "${unit}" (expected ${allowedUnits.join(" / ")}) — ignored`,
  "warning.styleOutOfRange.message": ({ property, value, min, max }) =>
    `${property}: ${value} is out of range ${formatRange(min, max)} — ignored`,
  "warning.styleUnknownProperty.message": ({ property }) =>
    `unknown style property "${property}" — ignored`,

  // Diagnostics (rendered in PreviewPane's diagnostic banner)
  "diagnostic.tokenTypeMismatch.message": ({ expected, got, value }) =>
    `Expected ${expected} but got ${got} ("${value}")`,
  "diagnostic.unexpectedTokenRoot.message": ({ tokenType, value }) =>
    `Unexpected token: ${tokenType} ("${value}")`,
  "diagnostic.topLevelDeclaration.message": ({ construct }) =>
    `A top-level ${construct} is not allowed — declare it inside a system block`,
  "diagnostic.unexpectedTokenInBlock.subResource": ({ tokenType, value }) =>
    `Unexpected token in sub-resource block: ${tokenType} ("${value}"). Sub-resource nodes (table, queue-item, bucket) cannot contain child declarations.`,
  "diagnostic.unexpectedTokenInBlock.generic": ({ tokenType, value }) =>
    `Unexpected token in block: ${tokenType} ("${value}")`,
  "diagnostic.unexpectedTokenInBlock.deployNode": ({ tokenType, value }) =>
    `Unexpected token in deploy node: ${tokenType} ("${value}")`,
  "diagnostic.unexpectedTokenInBlock.named": ({ blockKind, tokenType, value }) =>
    `Unexpected token in ${blockKind} block: ${tokenType} ("${value}")`,
  "diagnostic.expectedBraceOrString.message": ({ got, value }) =>
    `Expected { or string literal but got ${got} ("${value}")`,
  "diagnostic.expectedIdentifier.message": ({ got, value }) =>
    `Expected identifier but got ${got} ("${value}")`,
  "diagnostic.expectedStringAfter.message": ({ property }) =>
    `Expected string literal after "${property}"`,
  "diagnostic.propertyNotForNodeKind.role": `"role" property is only valid for user nodes`,
  "diagnostic.propertyNotForNodeKind.handles": `"handles" property is only valid for client and service nodes`,
  "diagnostic.propertyNotForNodeKind.delivers": `"delivers" property is only valid for service nodes`,
  "diagnostic.propertyNotForNodeKind.operations": `"operations" property is only valid for resource declarations inside a usecase`,
  "diagnostic.infraNotInContext.message": ({ infraKind, parentKind }) =>
    `"${infraKind}" is only valid as a direct child of system, not inside "${parentKind}"`,
  "diagnostic.entityNotInDomain.message": ({ parentKind }) =>
    `"entity" is only valid as a child of domain, not inside "${parentKind}"`,
  "diagnostic.nodeNotInContext.message": ({ childKind, parentKind }) =>
    `"${childKind}" is not a defined child of "${parentKind}"; it is drawn but carries no defined meaning there`,
  "diagnostic.legendNotTopLevel.message": ({ parentKind }) =>
    `legend blocks are only allowed at the top level of a file, not inside "${parentKind}"`,
  "diagnostic.expectedIdOrString.message": ({ context }) =>
    `Expected identifier or string literal after "${context}"`,
  "diagnostic.expectedNodeId.message": ({ kind }) =>
    `Expected identifier or string literal (id) after "${kind}"`,
  "diagnostic.invalidNodeKind.message": ({ kind }) => `Unexpected logical node kind: "${kind}"`,
  "diagnostic.expectedPropertyValue.message": ({ propName }) =>
    `Expected value for property "${propName}"`,
  "diagnostic.expectedIdAfter.message": ({ property }) =>
    `Expected identifier or string literal after "${property}"`,
  "diagnostic.teamPropertyRemoved.message": `"team" property has been removed; declare ownership with an organization block and "owns"`,
  "diagnostic.annotationParamUnsupported.message": ({ annotation, key }) =>
    `"@${annotation}" does not support the "${key}" parameter; it is ignored`,
  "diagnostic.linkUrlSchemeNotAllowed.message": ({ url, scheme }) =>
    scheme
      ? `link URL "${url}" uses a disallowed scheme "${scheme}" (allowed: http, https, mailto); the link is ignored`
      : `link URL "${url}" is not an absolute http / https / mailto URL; the link is ignored`,
  "diagnostic.edgeSourceMismatch.message": ({ from, parentId }) =>
    `Edge source "${from}" must match the enclosing block id "${parentId}"`,
  "diagnostic.clientResourceInvalidKind.message": ({ kind, name }) =>
    `Invalid client resource kind "${kind}" for resource "${name}". Allowed kinds: localStorage, sessionStorage, indexedDB, opfs, file, keychain`,
  "diagnostic.unknownResourceOperation.message": ({ operation, resourceId }) =>
    `Unknown resource operation "${operation}" on "${resourceId}". Recognized verbs: create, read, update, delete`,
  "diagnostic.duplicateResourceOperation.message": ({ operation, resourceId }) =>
    `Duplicate resource operation "${operation}" on "${resourceId}"`,
  "diagnostic.invalidCrudDecoration.message": ({ operation, value, resourceId }) =>
    `Invalid CRUD decoration "${value}" on operation "${operation}" of resource "${resourceId}". Right-hand side must be one of: create, read, update, delete`,
  "diagnostic.emptyCrudDecoration.message": ({ operation, resourceId }) =>
    `Empty CRUD decoration on operation "${operation}" of resource "${resourceId}". Use "${operation}:create,read,update,delete" or drop the colon`,
  "diagnostic.duplicateCrudDecorationTarget.message": ({ operation, value, resourceId }) =>
    `Duplicate CRUD verb "${value}" in decoration of "${operation}" on resource "${resourceId}"`,
  "diagnostic.duplicateOwnerAssignment.message": ({ nodeId, existingTeam }) =>
    `"${nodeId}" is owned by more than one team; "${existingTeam}" is kept as its primary owner`,
  "diagnostic.duplicateBoundaryAssignment.message": ({ nodeId, existingBoundary }) =>
    `"${nodeId}" belongs to more than one boundary (including "${existingBoundary}")`,
  "diagnostic.boundaryMembershipNotDrawn.message": ({ nodeId, boundaryId }) =>
    `The "${boundaryId}" frame does not reach "${nodeId}" in this view; the membership is marked on the card`,
  "diagnostic.containsTargetNotFound.message": ({ memberId }) =>
    `"${memberId}" referenced in "contains" was not found in the system hierarchy`,
  "diagnostic.containsTargetAmbiguous.message": ({ path, candidates }) =>
    `"${path}" referenced in "contains" matches multiple nodes of different kind or depth: ${candidates
      .map((c) => `${c.path} (${c.kind})`)
      .join(", ")}. Qualify with a longer path to name one`,
  "diagnostic.boundaryNotInContext.message": ({ parentKind }) =>
    `"boundary" cannot be declared inside "${parentKind}", which draws no canvas of its own`,
  "diagnostic.duplicateBoundaryId.message": ({ boundaryId }) =>
    `Duplicate boundary id "${boundaryId}" in the same scope`,
  "diagnostic.duplicateFacetId.message": ({ facetId }) =>
    `Facet "${facetId}" is already declared; the first declaration is the one references resolve to`,
  "diagnostic.positionalLabelRemoved.message": ({ construct }) =>
    `"${construct}" does not accept a label after its id; write label "..." inside the block`,
  "diagnostic.duplicateTeamId.message": ({ teamId }) => `Duplicate team id "${teamId}"`,
  "diagnostic.nodeIdMultipleLocations.message": ({ nodeId }) =>
    `Node id "${nodeId}" appears in multiple locations; first path is used for navigation`,
  "diagnostic.duplicateNodeIdParent.message": ({ nodeId }) =>
    `Duplicate node id "${nodeId}" under the same parent`,
  "diagnostic.ownsTargetNotFound.message": ({ ownedId }) =>
    `"${ownedId}" referenced in "owns" was not found in the system hierarchy`,
  "diagnostic.ownsTargetAmbiguous.message": ({ path, candidates }) =>
    `"${path}" referenced in "owns" matches multiple nodes of different kind or depth: ${candidates
      .map((c) => `${c.path} (${c.kind})`)
      .join(", ")}. Qualify with a longer path to name one`,
  "diagnostic.unresolvedResourceRef.message": ({ infraId, subId, missing }) =>
    missing === "block"
      ? `"resource ${infraId}.${subId}" references "${infraId}", which no database / queue / storage block declares`
      : `"resource ${infraId}.${subId}" references "${subId}", which "${infraId}" does not declare`,
  "diagnostic.unresolvedTableRef.message": ({ entityId, infraId, subId, missing }) =>
    missing === "block"
      ? `Entity "${entityId}" maps to "${infraId}.${subId}", but no database / queue / storage block declares "${infraId}"`
      : `Entity "${entityId}" maps to "${infraId}.${subId}", but "${infraId}" does not declare "${subId}"`,
  "diagnostic.duplicateEdgeId.message": ({ authorId }) =>
    `Duplicate edge id "#${authorId}"; edge ids must be unique within a system`,
  "diagnostic.ambiguousEdgeBase.message": ({ fromId, toId, arrow }) =>
    `Multiple edges share the base "${fromId}${arrow}${toId}" with no #<id> to disambiguate; per-edge style selectors will not match any of them`,
  "diagnostic.styleTokenTypeMismatch.message": ({ expected, got, value }) =>
    `Expected ${expected} but got ${got} ("${value}")`,
  "diagnostic.expectedStylePropertyName.message": ({ got }) =>
    `Expected property name but got ${got}`,
  "diagnostic.expectedSemicolonBetweenProperties.message": ({ property }) =>
    `Expected ";" after "${property}" but found ","; properties are separated by semicolons`,
  "diagnostic.unknownEdgeSelectorAttribute.message": ({ attribute }) =>
    `Unknown selector attribute "${attribute}"; only "from", "to" and "facets" are supported (e.g. edge[from=Hub], service[facets=pii])`,
  "diagnostic.styleInvalidEnumValue.message": ({ property, value, allowed }) =>
    `Invalid value for "${property}": "${value}". Allowed: ${allowed.join(", ")}`,
  "diagnostic.styleInvalidHexColor.message": ({ property, value }) =>
    `Invalid hex color for "${property}": "${value}" (expected #RGB / #RGBA / #RRGGBB / #RRGGBBAA)`,
  "diagnostic.styleMissingLengthUnit.message": ({ property, value, allowedUnits }) =>
    `Missing unit for "${property}": "${value}". Expected one of: ${allowedUnits.join(", ")}`,
  "diagnostic.styleInvalidLengthUnit.message": ({ property, value, unit, allowedUnits }) =>
    `Invalid unit "${unit}" for "${property}": "${value}". Allowed: ${allowedUnits.join(", ")}`,
  "diagnostic.styleOutOfRange.message": ({ property, value, min, max }) =>
    `Value ${value} for "${property}" is out of range ${formatRange(min, max)}`,
  "diagnostic.styleUnknownProperty.message": ({ property }) =>
    `Unknown style property "${property}"`,
  "diagnostic.circularImport.message": ({ filePath }) => `Circular import detected: ${filePath}`,
  "diagnostic.fileNotFound.message": ({ filePath }) => `File not found: ${filePath}`,
  "diagnostic.directoryNotFound.message": ({ dirPath }) => `Directory not found: ${dirPath}`,
  "diagnostic.serviceOutsideSystem.message": ({ serviceId }) =>
    `"${serviceId}" is declared outside any system block — system membership is ambiguous`,
  "diagnostic.duplicateNodeInSystem.message": ({ nodeId, systemId }) =>
    `Duplicate node ID "${nodeId}" in system "${systemId}"`,
  "diagnostic.duplicateNodeInDeploy.message": ({ nodeId, deployId }) =>
    `Duplicate node ID "${nodeId}" in deploy block "${deployId}"`,
  "diagnostic.duplicateTeamInOrganization.message": ({ teamId, orgId }) =>
    `Duplicate team ID "${teamId}" in organization "${orgId}"`,
  "diagnostic.systemPropertyConflict.message": ({
    blockKind,
    blockId,
    property,
    chosen,
    ignored,
  }) => `${blockKind} "${blockId}" ${property} conflict — using "${chosen}", ignoring "${ignored}"`,
  "diagnostic.infraLeafRedeclaredSilently.message": ({ leafKind, leafId, infraKind, infraId }) =>
    `${leafKind} "${leafId}" is declared more than once inside ${infraKind} "${infraId}"; karasu kept the first declaration.`,
  "diagnostic.infraRedeclaredAcrossFiles.message": ({ blockKind, blockId }) =>
    `${blockKind} "${blockId}" is declared in multiple files; karasu merged them.`,
  "diagnostic.importIdNotFound.message": ({ id, path }) =>
    `Imported identifier "${id}" not found in ${path}`,
  "diagnostic.importPathNotFound.message": ({
    path,
    failedAt,
    failedSegment,
    importPath,
    lastResolvedId,
  }) =>
    lastResolvedId
      ? `Import path "${path}" failed at segment "${failedSegment}" (#${failedAt}): no child with that id under "${lastResolvedId}"`
      : `Import path "${path}" failed at segment "${failedSegment}" (#${failedAt}): no top-level system with that id in ${importPath}`,
  "diagnostic.circularStyleImport.message": ({ filePath }) =>
    `Circular style import detected: ${filePath}`,
  "diagnostic.styleFileNotFound.message": ({ filePath }) => `Style file not found: ${filePath}`,
  "diagnostic.appProjectCompileError.message": "An error occurred while compiling the project",
  "diagnostic.appOrgParseError.message": "An error occurred during parsing",
};
